import { compare } from 'compare-versions';

import { secureModeFirmwareVersions } from './constants';
import type { BluetoothConfig } from 'src/types/BleTypes';

/**
 * Parse a dotted firmware version from device settings (e.g. `1.6.2` from
 * `20250101-120000/1.6.2-githash`).
 */
export const parseBleFwVersion = (settings: any): string | null => {
	const raw = settings?.DeviceInfo?.ver || settings?.sys?.device?.fw_id || settings?.DeviceInfo?.fw_id;
	return raw?.match(/(\d{1,}\.){1,}\d{1,}/)?.[0] ?? null;
};

export const isBleFwAtLeast = (settings: any, version: string): boolean => {
	const fwVersion = parseBleFwVersion(settings);
	if (!fwVersion) return false;
	try {
		return compare(fwVersion, version, '>=');
	} catch {
		return false;
	}
};

/**
 * True when firmware exposes the 2.0.0 BLE pairing contract:
 * `ble.enable` removed, `Ble.StartPairing` / `ListPairedDevices` / status `flags`.
 *
 * Capability fallback: config has `ble.rpc` and no `ble.enable`.
 * Do not use `isFwAfter200` (that still gates on 1.8.9).
 */
export const hasBlePairingApi = (settings: any): boolean => {
	if (isBleFwAtLeast(settings, '2.0.0')) return true;
	const ble = settings?.ble;
	return Boolean(ble && typeof ble.enable === 'undefined' && ble.rpc);
};

/**
 * Observer / CloudRelay scan is automatic (no `observer.enable` toggle).
 * True from 1.5.0, pairing FW, or when the device already reports always-on.
 */
export const isBleObserverAutomatic = (settings: any, status?: any): boolean => {
	if (hasBlePairingApi(settings) || isBleFwAtLeast(settings, '1.5.0')) return true;
	return (
		typeof settings?.ble?.observer?.always_on !== 'undefined' ||
		typeof status?.sys?.btrelay_rev !== 'undefined'
	);
};

/** Strip BLE address type suffix (`AA:BB:CC:DD:EE:FF,p` → `AA:BB:CC:DD:EE:FF`). */
export const stripBleAddrTypeSuffix = (value?: string | null): string => {
	if (!value) return '';
	return value.split(',')[0] ?? '';
};

/** Normalises a BLE MAC to uppercase colon-separated form (AA:BB:CC:DD:EE:FF). */
export const formatBleMacAddress = (mac: string): string => {
	if (!mac) return '';
	const hex = mac.replace(/[^0-9a-fA-F]/g, '').toUpperCase();
	if (hex.length !== 12) return '';
	return hex.match(/.{1,2}/g)?.join(':') ?? '';
};

/**
 * First usable MAC from status/RPC sources. Status `addr` wins over `mac`.
 */
export const pickBleMacFromSources = (sources: BleMacSources): string => {
	const candidates = [
		stripBleAddrTypeSuffix(sources.statusAddr),
		stripBleAddrTypeSuffix(sources.statusMac),
		stripBleAddrTypeSuffix(sources.rpcAddr),
		stripBleAddrTypeSuffix(sources.rpcMac),
	];
	for (const candidate of candidates) {
		const formatted = formatBleMacAddress(candidate);
		if (formatted) return formatted;
	}
	return '';
}
/** Firmware 2.0.0+ no longer has a global `ble.enable` flag. Alias of `hasBlePairingApi`. */
export const firmwareOmitsBleEnable = hasBlePairingApi;

/**
 * Accepts GetConfig-shaped settings or inclusion `deviceInfo` (`{ ver }`).
 */
export const asBleSettings = (source?: any): any => {
	if (!source) return source;
	if (source.DeviceInfo || source.sys) return source;
	if (typeof source.ver === 'string') {
		return { DeviceInfo: { ver: source.ver }, ble: source.ble };
	}
	return source;
};

/**
 * Checks if the device firmware version is >= `secureModeFirmwareVersions` (1.8.9).
 * In FW 2.0.0+ the global `ble.enable` flag is removed — BLE auto-activates.
 *
 * NOTE: this still compares to `secureModeFirmwareVersions` (`1.8.9`).
 * Use `firmwareOmitsBleEnable` / `hasBlePairingApi` for the 2.0.0 enable-flag contract.
 */
export const isFwAfter200 = (settings: any): boolean =>
	isBleFwAtLeast(settings, secureModeFirmwareVersions);

export type BleSetConfigInput = {
	enable?: boolean;
	observerEnable?: boolean;
	includeObserver?: boolean;
	rpcEnable?: boolean;
};

export type BleMacSources = {
	statusMac?: string;
	statusAddr?: string;
	rpcMac?: string;
	rpcAddr?: string;
};

/**
 * Ble.SetConfig payload. Firmware 2.0.0+ must not send `enable` or `observer`.
 * Older firmware keeps `{ enable, observer?, rpc? }` as today.
 */
export const buildBleSetConfig = (settings: any, input: BleSetConfigInput = {}): BluetoothConfig => {
	if (firmwareOmitsBleEnable(asBleSettings(settings))) {
		if (typeof input.rpcEnable !== 'boolean') return {};
		return { rpc: { enable: input.rpcEnable } };
	}

	const config: BluetoothConfig = {};
	if (typeof input.enable === 'boolean') {
		config.enable = input.enable;
	}
	if (input.includeObserver || typeof input.observerEnable === 'boolean') {
		config.observer = { enable: Boolean(input.observerEnable) };
	}
	if (typeof input.rpcEnable === 'boolean') {
		config.rpc = { enable: input.rpcEnable };
	}
	return config;
};

/**
 * BLE "switch" for last-connection / connectivity counts.
 * 2.0.0+ has no `ble.enable` — count RPC instead. Older FW keeps `ble.enable`.
 */
export const bleConnectivityEnabled = (settings: any): boolean =>
	firmwareOmitsBleEnable(settings)
		? Boolean(settings?.ble?.rpc?.enable)
		: Boolean(settings?.ble?.enable);

/** Same flags as `isLastAPConnection` (Wi-Fi / Ethernet / BLE connectivity). */
export const isLastApConnectionFromSettings = (settings: any): boolean => {
	const countConn = [
		settings?.wifi?.sta?.enable,
		settings?.wifi?.sta1?.enable,
		settings?.wifi?.ap?.enable,
		settings?.eth?.enable,
		bleConnectivityEnabled(settings),
	].reduce((n, flag) => n + (Number(flag) || 0), 0);
	return countConn < 2;
};

/**
 * Gateway helpers: 2.0.0 (or capability fallback) plus existing 1.8.9+ short-circuit.
 */
export const isBleGatewayFwAlwaysOn = (settings: any): boolean =>
	firmwareOmitsBleEnable(settings) || isFwAfter200(settings);

/**
 * Pure form of `isGatewayObserverEnabled` for tests (no device store).
 */
export const isGatewayObserverEnabledFromSettings = (settings: any, status?: any): boolean => {
	if (isBleGatewayFwAlwaysOn(settings)) return true;
	return Boolean(
		settings?.ble?.enable &&
		(typeof status?.sys?.btrelay_rev !== 'undefined' ||
			(typeof settings?.ble?.observer !== 'undefined' &&
				(settings?.ble?.observer?.always_on || settings?.ble?.observer?.enable))),
	);
};

/**
 * Post-inclusion BLE disable payload.
 * Pre-2.0.0: `enable: false` plus RPC off.
 * 2.0.0+: `ble.enable` is gone — RPC off only.
 */
export const buildBleDisableConfig = (settings?: any): BluetoothConfig => {
	if (firmwareOmitsBleEnable(asBleSettings(settings))) {
		return { rpc: { enable: false } };
	}
	return {
		enable: false,
		rpc: { enable: false },
	};
};
